import { api } from "@/lib/api";
import {
  HotelRoom,
  HotelRoomDtoSchema,
  hotelRoomFromDto,
  hotelRoomToDto,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import {
  getAllHotelRoomsQueryOptions,
  getHotelRoomQueryOptions,
  getHotelRoomsQueryOptions,
} from "./get.hooks";

export function useUpdateHotelRoom(
  hotelId: string,
  opts?: {
    onSuccess?: (room: HotelRoom) => void;
    onError?: () => void;
  },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (room: HotelRoom) => {
      const dto = hotelRoomToDto(room);
      const result = await api.put("/api/hotel-rooms", dto, HotelRoomDtoSchema);
      return hotelRoomFromDto(result);
    },
    onSuccess: (room) => {
      queryClient.setQueryData(
        getHotelRoomsQueryOptions(hotelId).queryKey,
        (old) => old?.map((r) => (r.id !== room.id ? r : room)),
      );
      queryClient.setQueryData(getAllHotelRoomsQueryOptions.queryKey, (old) =>
        old?.filter((r) => (r.id !== room.id ? r : room)),
      );
      queryClient.setQueryData(
        getHotelRoomQueryOptions(room.id).queryKey,
        () => room,
      );
      toast({
        title: "toast.hotelRoomUpdated",
        variant: "success",
      });
      opts?.onSuccess?.(room);
    },
    onError: () => {
      toast({
        title: "toast.updateHotelRoomError",
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
